import { Platform, StyleSheet } from "react-native";

const inter = (weight: "400" | "500" | "600" | "700") =>
  Platform.select({
    ios: {
      fontFamily:
        weight === "400"
          ? "Inter-Regular"
          : weight === "500"
          ? "Inter-Medium"
          : weight === "600"
          ? "Inter-SemiBold"
          : "Inter-Bold",
      fontWeight: weight as any,
    },
    android: {
      fontFamily:
        weight === "400"
          ? "Inter-Regular"
          : weight === "500"
          ? "Inter-Medium"
          : weight === "600"
          ? "Inter-SemiBold"
          : "Inter-Bold",
      fontWeight: weight as any,
    },
    default: { fontFamily: "Inter, sans-serif", fontWeight: weight as any },
  }) as { fontFamily: string; fontWeight: any };

export default StyleSheet.create({
  // ── Screen root ───────────────────────────────────────────────────────────
  screenRoot: {
    flex: 1,
    backgroundColor: "#f5f9ff",
  },
  container: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 36,
    paddingTop: 24,
  },
  content: { flex: 1 },
  scrollContent: { paddingBottom: 48 },

  // ── Page header ───────────────────────────────────────────────────────────
  pageHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  pageTitleBlock: { flex: 1, minWidth: 0 },
  breadcrumbRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },
  breadcrumbLink: { fontSize: 12, color: "#585858", ...inter("500") },
  breadcrumbSeparator: { fontSize: 12, color: "#818181" },
  breadcrumbCurrent: { fontSize: 12, color: "#3a3a3a", ...inter("600") },
  pageTitle: { fontSize: 24, color: "#2d2d2d", lineHeight: 30, ...inter("700") },
  pageSubtitle: { fontSize: 13, color: "#818181", marginTop: 2, ...inter("400") },

  pageActions: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    flexShrink: 0,
  },

  // ── Buttons ───────────────────────────────────────────────────────────────
  btnPrimary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#034895",
    borderRadius: 100,
    paddingHorizontal: 18,
    paddingVertical: 9,
  },
  btnPrimaryPressed: { backgroundColor: "#01346F" },
  btnDisabled: { backgroundColor: "#D0D0D0", opacity: 0.6 },
  btnPrimaryText: { color: "#ffffff", fontSize: 13, ...inter("600") },

  btnSecondary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#ffffff",
    borderRadius: 100,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "#D7E5F8",
  },
  btnSecondaryPressed: { backgroundColor: "#f3f7fd" },
  btnSecondaryText: { color: "#034895", fontSize: 13, ...inter("600") },

  btnGhost: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "transparent",
    borderRadius: 100,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  btnGhostPressed: { backgroundColor: "#e7effb" },
  btnGhostText: { color: "#4b4b4b", fontSize: 13, ...inter("500") },

  btnGhostSm: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "transparent",
    borderRadius: 100,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  btnGhostSmText: { color: "#4b4b4b", fontSize: 12, ...inter("500") },

  btnDangerSm: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "transparent",
    borderRadius: 100,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  btnDangerSmPressed: { backgroundColor: "#FFE5DE" },
  btnDangerSmText: { color: "#C12C01", fontSize: 12, ...inter("500") },

  // ── Tab bar ───────────────────────────────────────────────────────────────
  tabBar: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#DFDFDF",
    marginBottom: 16,
    gap: 2,
  },
  tab: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  tabActive: { borderBottomColor: "#034895" },
  tabText: { fontSize: 13, color: "#585858", ...inter("500") },
  tabTextActive: { color: "#034895", ...inter("600") },
  tabCount: {
    backgroundColor: "#EBEBEB",
    borderRadius: 100,
    paddingHorizontal: 7,
    paddingVertical: 2,
    minWidth: 22,
    alignItems: "center",
  },
  tabCountActive: { backgroundColor: "#C6DCFB" },
  tabCountText: { fontSize: 11, color: "#585858", ...inter("600") },
  tabCountTextActive: { color: "#034895" },

  // ── Read-filter chips ─────────────────────────────────────────────────────
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
    flexWrap: "wrap",
    gap: 8,
  },
  filterChips: { flexDirection: "row", gap: 6 },
  filterChip: {
    borderRadius: 100,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: "#DFDFDF",
    backgroundColor: "#ffffff",
  },
  filterChipActive: {
    backgroundColor: "#034895",
    borderColor: "#034895",
  },
  filterChipText: { fontSize: 12, color: "#4b4b4b", ...inter("500") },
  filterChipTextActive: { color: "#ffffff" },

  resultCount: { fontSize: 12, color: "#818181", ...inter("400") },

  // ── Notification row ──────────────────────────────────────────────────────
  notifRow: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    borderLeftWidth: 4,
    borderLeftColor: "#838383",
    marginBottom: 10,
    ...Platform.select({
      web: { boxShadow: "0 1px 4px rgba(0,30,70,0.06)" } as any,
      default: {
        shadowColor: "#001E46",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.06,
        shadowRadius: 4,
        elevation: 1,
      },
    }),
  },
  notifRowIncident: { borderLeftColor: "#C12C01" },
  notifRowMaint: { borderLeftColor: "#005CBC" },
  notifRowGeneral: { borderLeftColor: "#838383" },
  notifRowUnread: { backgroundColor: "#fafcff" },

  notifInner: { padding: 16 },

  notifTop: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 6,
    gap: 12,
  },
  notifTitleBlock: { flex: 1, minWidth: 0 },
  notifTitle: { fontSize: 14, color: "#2d2d2d", lineHeight: 20, ...inter("600") },
  notifTitleUnread: { ...inter("700") },

  notifBadgeRow: {
    flexDirection: "row",
    gap: 6,
    flexWrap: "wrap",
    marginTop: 4,
  },

  notifActions: { flexDirection: "row", gap: 2, alignItems: "center", flexShrink: 0 },

  notifMsg: {
    fontSize: 13,
    color: "#4b4b4b",
    lineHeight: 19,
    marginBottom: 10,
    ...inter("400"),
  },

  notifReadMoreText: {
    fontSize: 13,
    color: "#005CBC",
    fontWeight: "600",
    ...inter("600"),
  },

  notifFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
  },
  notifMeta: { flexDirection: "row", flexWrap: "wrap", gap: 10, flex: 1 },
  notifMetaItem: { flexDirection: "row", alignItems: "center", gap: 4 },
  notifMetaText: { fontSize: 11, color: "#818181", ...inter("400") },
  notifMetaLink: { fontSize: 11, color: "#005CBC", ...inter("500") },

  notifStats: { flexDirection: "row", gap: 14, flexShrink: 0 },
  notifStat: { alignItems: "center" },
  notifStatNum: { fontSize: 15, color: "#2d2d2d", ...inter("700") },
  notifStatLabel: { fontSize: 10, color: "#818181", ...inter("400") },

  // ── Badge ─────────────────────────────────────────────────────────────────
  badge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderRadius: 100,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeDot: { width: 6, height: 6, borderRadius: 3 },
  badgeText: { fontSize: 11, ...inter("600") },

  // type badges
  badgeIncident: { backgroundColor: "#FFE5DE" },
  badgeIncidentText: { color: "#C12C01" },
  badgeIncidentDot: { backgroundColor: "#C12C01" },
  badgeMaint: { backgroundColor: "#DAEEFA" },
  badgeMaintText: { color: "#034895" },
  badgeMaintDot: { backgroundColor: "#034895" },
  badgeGeneral: { backgroundColor: "#EBEBEB" },
  badgeGeneralText: { color: "#4b4b4b" },
  badgeGeneralDot: { backgroundColor: "#838383" },

  // status badges
  badgeActive: { backgroundColor: "#DDF9DA" },
  badgeActiveText: { color: "#027C06" },
  badgeActiveDot: { backgroundColor: "#027C06" },
  badgeHomepage: { backgroundColor: "#E8F0FB" },
  badgeHomepageText: { color: "#034895" },

  // unread dot indicator
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#034895",
    marginTop: 3,
    marginRight: 4,
  },

  // ── Empty state ───────────────────────────────────────────────────────────
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 48,
    gap: 12,
  },
  emptyIconWrap: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: "#E8F0FB",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
  },
  emptyTitle: { fontSize: 16, color: "#2d2d2d", ...inter("600") },
  emptyDesc: { fontSize: 13, color: "#818181", textAlign: "center", maxWidth: 280, ...inter("400") },

  // ── Skeleton loader ───────────────────────────────────────────────────────
  skeletonRow: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    borderLeftWidth: 4,
    borderLeftColor: "#e6e6e6",
    padding: 16,
    marginBottom: 10,
    gap: 8,
  },
  skeletonLine: { height: 12, borderRadius: 6, backgroundColor: "#ebebeb" },
  skeletonLineShort: { width: "60%", height: 10, borderRadius: 5, backgroundColor: "#ebebeb" },

  // ── Main layout (two-panel) ───────────────────────────────────────────────
  panelRow: {
    flexDirection: "row",
    gap: 20,
    alignItems: "flex-start",
    flex: 1,
  },
  leftPanel: { flex: 1, minWidth: 0 },
  rightPanel: {
    width: 380,
    flexShrink: 0,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e6e6e6",
    overflow: "hidden",
    ...Platform.select({
      web: { boxShadow: "0 2px 12px rgba(0,30,70,0.07)" } as any,
      default: {
        shadowColor: "#001E46",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.07,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
  },

  // ── Communication panel ───────────────────────────────────────────────────
  commHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
  },
  commTitleRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  commTitle: { fontSize: 14, color: "#2d2d2d", ...inter("600") },
  commSubtitle: { fontSize: 11, color: "#818181", ...inter("400") },
  commFilter: { fontSize: 12, color: "#005CBC", ...inter("500") },

  commBody: {
    flex: 1,
    maxHeight: 540,
  },
  commBodyContent: { padding: 16, gap: 0 },

  // ── Message / Activity item ───────────────────────────────────────────────
  msgItem: {
    flexDirection: "row",
    gap: 10,
    paddingBottom: 16,
    position: "relative",
  },
  msgAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 2,
  },
  msgAvatarText: { fontSize: 9, color: "#ffffff", ...inter("700") },

  msgContent: { flex: 1, minWidth: 0 },
  msgTopRow: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 6,
    marginBottom: 4,
    flexWrap: "wrap",
  },
  msgAuthor: { fontSize: 13, color: "#2d2d2d", ...inter("600") },
  msgRole: {
    fontSize: 11,
    color: "#818181",
    backgroundColor: "#EBEBEB",
    borderRadius: 100,
    paddingHorizontal: 7,
    paddingVertical: 1,
    ...inter("500"),
  },
  msgRoleSustain: { backgroundColor: "#E1E3FD", color: "#5C4EC9" },
  msgRoleServicenow: { backgroundColor: "#CCEAF6", color: "#0078A7" },
  msgRoleSystem: { backgroundColor: "#EBEBEB", color: "#585858" },
  msgTime: { fontSize: 11, color: "#818181", ...inter("400"), marginLeft: "auto" },
  msgBody: { fontSize: 13, color: "#4b4b4b", lineHeight: 18, ...inter("400") },
  msgBodySystem: {
    fontSize: 12,
    color: "#585858",
    backgroundColor: "#F8F8F8",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    ...inter("400"),
  },
  msgAttachment: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 6,
    backgroundColor: "#E8F0FB",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    alignSelf: "flex-start",
  },
  msgAttachmentText: { fontSize: 12, color: "#034895", ...inter("500") },

  // connector line between messages
  msgConnector: {
    position: "absolute",
    left: 13,
    top: 30,
    bottom: 0,
    width: 1,
    backgroundColor: "#EBEBEB",
  },

  // ── Comment / message composer ────────────────────────────────────────────
  composer: {
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
    padding: 12,
  },
  composerInput: {
    borderWidth: 1,
    borderColor: "#D0D0D0",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 13,
    color: "#2d2d2d",
    minHeight: 72,
    textAlignVertical: "top",
    backgroundColor: "#fafafa",
    ...inter("400"),
  },
  composerInputFocused: {
    borderColor: "#005CBC",
    backgroundColor: "#ffffff",
  },
  composerFooter: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
  },
  composerHint: { fontSize: 11, color: "#818181", ...inter("400") },
  composerActions: { flexDirection: "row", gap: 6, alignItems: "center" },

  composerAttachBtn: {
    width: 32,
    height: 32,
    borderRadius: 100,
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  composerAttachBtnActive: { backgroundColor: "#E8F0FB" },
  composerAttachBtnPressed: { backgroundColor: "#e7effb" },

  composerSendBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: "#034895",
    borderRadius: 100,
    paddingHorizontal: 16,
    paddingVertical: 7,
  },
  composerSendBtnDisabled: { backgroundColor: "#D0D0D0" },
  composerSendBtnPressed: { backgroundColor: "#01346F" },
  composerSendBtnText: { color: "#ffffff", fontSize: 12, ...inter("600") },

  attachPendingChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    backgroundColor: "#E8F0FB",
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    marginTop: 6,
    alignSelf: "flex-start",
  },
  attachPendingText: { fontSize: 12, color: "#034895", ...inter("500") },
  attachRemoveBtn: { padding: 2 },
});
